/**
 * Turning an accepted AI proposal into an ordinary saved search.
 *
 * "Ordinary" is the point: the same `POST /me/saved-searches` the manual flow
 * uses, so an AI-built subscription is matched by the same sweep, capped by the
 * same digest ceiling and editable in the same builder. A separate write path
 * would have given this feature its own matching bugs to discover.
 */
import { createNiche, type NicheFirstMatch, type SavedSearch } from './saved-search-client';
import { draftToCreateInput, type AiDraft } from './ai-proposal-draft';

export interface SavedAiSearch {
  search: SavedSearch;
  /** The instant first match, for onboarding's sample-alert preview. */
  firstMatch: NicheFirstMatch | null;
}

/**
 * Create the search. One call, the whole proposal.
 *
 * There used to be a second `PATCH` here (and a `partial` flag on the result
 * saying it had failed), because create could not express
 * regions or free-text words in the plural: it carried `regionCode`/`keyword`
 * (the first of each) and the tail followed. That is fixed on the API side, and
 * removing the PATCH removes three real problems with it — the window in which
 * the search matched one region and one word, the "saved but not quite" state
 * when the second call failed, and the fact that the server compared a
 * first-word-only payload against the proposal and so recorded `edited_saved`
 * for people who had changed nothing at all.
 */
export async function saveAiDraft(
  draft: AiDraft,
  opts: { draftId?: string; replace?: boolean } = {},
): Promise<SavedAiSearch> {
  const created = await createNiche(
    draftToCreateInput(draft, { aiDraftId: opts.draftId, replace: opts.replace }),
  );
  return { search: created.search, firstMatch: created.firstMatch };
}
